import React from 'react';
import { Link, useNavigate } from '@reach/router';
import styled from 'styled-components';
import { useApolloClient, useMutation } from '@apollo/client';
import { white } from '@avangard/ui/colors';
import { Divider, Hidden, useMediaQuery, useTheme } from '@material-ui/core';
import { Group, Person } from '@material-ui/icons';
import { breakpoints } from '@avangard/ui/utils';

import { RouteMenuLocationEnum, routes } from '@config/routes';
import { createRouteUrl, getMenuRoutes } from '@lib/routing';
import { TokenSession } from '@lib/token';
import { AvangardLogo, SidebarLogo, StockLogo } from '@modules/layout/icons';
import { blue } from '@modules/layout/styled';
import { useCurrentUser } from '@modules/auth/hooks';
import { RevokeTokenMutation } from '@modules/auth/graphql';

import type { RevokeTokenMutationType, RevokeTokenMutationVariables } from '@modules/types/graphql';
import type { LinkGetProps } from '@reach/router';

type SidebarProps = {
    open: boolean;
    onClose?: () => void;
};

const itemActiveStyles = {
    color: white[100],
    backgroundColor: blue[100],
};

const Root = styled.aside<Pick<SidebarProps, 'open'>>`
    display: flex;
    flex-direction: column;
    position: fixed;
    top: 0;
    left: 0;
    width: 245px;
    height: 100%;
    background-image: url('/images/main-background.jpg');
    background-size: 100% auto;
    transition: 0.23s ease-in-out;
    z-index: 89;
    overflow-x: hidden;
    overflow-y: auto;

    ${breakpoints.down('sm')} {
        width: 100%;
        padding-top: 64px;
        padding-bottom: 74px;
        opacity: ${p => Number(p.open)};
        visibilty: ${p => (p.open ? 'visible' : 'hidden')};
        pointer-events: ${p => (p.open ? 'auto' : 'none')};
        transform: ${p => (p.open ? 'translateY(0)' : 'translateY(20px)')};
    }
`;

const Logo = styled.div`
    width: 100%;
`;

const List = styled.div`
    display: flex;
    flex-wrap: wrap;
    margin-top: 4px;

    > * {
        &:not(:last-of-type) {
            margin-bottom: 8px;
        }
    }
`;

const ListItem = styled.div`
    cursor: pointer;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    width: 100%;
    height: 40px;
    padding: 0 16px 0 24px;
    color: ${white[60]};

    &:hover {
        color: ${white[100]};
    }
`;

const ListItemIcon = styled.div`
    display: flex;
    width: 16px;
    height: 16px;
`;

const ListItemContent = styled.div`
    width: calc(100% - 28px);
    margin-left: 12px;
    font-size: 1.4rem;
    line-height: 16px;
`;

const StyledDivider = styled(Divider)`
    background-color: ${white[60]};
    opacity: 0.5;
    margin: 16px 24px;
`;

const Footer = styled.div`
    position: absolute;
    left: 0;
    bottom: 0;
    padding: 20px;
`;

const Logos = styled.div`
    //margin-bottom: 20px;
    max-width: 80%;
    display: flex;
    align-items: center;
    justify-content: center;

    & > *:not(:last-child) {
        margin-right: 16px;
    }
`;

const Support = styled.div`
    color: ${white[100]};
    font-size: 1.4rem;
    line-height: 20px;

    a {
        color: inherit;
    }
`;

const personIcon = <Person style={{ fontSize: 18 }} />;
const groupIcon = <Group style={{ fontSize: 18 }} />;

const Sidebar = (props: SidebarProps): React.ReactElement => {
    const { open, onClose } = props;

    const navigate = useNavigate();
    const apolloClient = useApolloClient();

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'), { noSsr: true });

    const { currentUser } = useCurrentUser({ fetchPolicy: 'cache-first' });

    const menuRoutes = getMenuRoutes(RouteMenuLocationEnum.sidebar, {
        forRoles: [currentUser.role],
    });

    const isCurrent = React.useCallback(
        (linkProps: LinkGetProps) => ({
            style: linkProps.isCurrent || linkProps.isPartiallyCurrent ? itemActiveStyles : {},
        }),
        [],
    );

    const profileLink = createRouteUrl(routes.profile);

    const [logout, { loading: logoutLoading }] =
        useMutation<RevokeTokenMutationType, RevokeTokenMutationVariables>(RevokeTokenMutation);

    const handleClickLogout = async (): Promise<void> => {
        const refreshToken = TokenSession.getCurrentSession().getRefreshToken().getToken();

        if (!refreshToken || logoutLoading) {
            return;
        }

        try {
            await logout({
                variables: { refreshToken },
            });
        } catch (e) {
            console.log(e);
        } finally {
            TokenSession.destroyCurrentSession();
            await apolloClient.clearStore();
            await navigate(routes.login.path, { replace: true });
            if (isMobile) {
                onClose?.();
            }
        }
    };

    const handleClickNavigate =
        (path: string) =>
        (event: React.MouseEvent<unknown>): void => {
            event.preventDefault();

            if (isMobile) {
                onClose?.();
            }

            navigate(path);
        };

    return (
        <Root open={open}>
            <Hidden smDown>
                <Logo>
                    <SidebarLogo />
                </Logo>
            </Hidden>

            {menuRoutes.length ? (
                <List>
                    {menuRoutes.map(menuRoute => {
                        const Icon = menuRoute.menuIcon;

                        const originMenuTitle = menuRoute.menuTitle ?? menuRoute.title;

                        return (
                            <ListItem
                                as={Link}
                                key={menuRoute.name}
                                to={menuRoute.path}
                                getProps={isCurrent}
                                onClick={handleClickNavigate(menuRoute.path)}
                            >
                                <ListItemIcon>
                                    {Icon ? <Icon style={{ fontSize: 16 }} /> : null}
                                </ListItemIcon>

                                <ListItemContent>{originMenuTitle}</ListItemContent>
                            </ListItem>
                        );
                    })}
                </List>
            ) : null}

            <Hidden mdUp>
                <StyledDivider light variant='middle' />

                <List>
                    <ListItem as='div' onClick={handleClickNavigate(profileLink)}>
                        <ListItemIcon>{personIcon}</ListItemIcon>
                        <ListItemContent>Профиль</ListItemContent>
                    </ListItem>

                    <ListItem as='div' onClick={handleClickLogout}>
                        <ListItemIcon>{groupIcon}</ListItemIcon>
                        <ListItemContent>Выйти</ListItemContent>
                    </ListItem>
                </List>
            </Hidden>

            <Hidden smDown>
                <Footer>
                    <Logos>
                        <StockLogo />
                        <AvangardLogo />
                    </Logos>
                    {/*<Support>*/}
                    {/*    <p>*/}
                    {/*        По техническим вопросам: <br />*/}
                    {/*        <a href='mailto:info@avangard-online.ru'>info@avangard-online.ru</a>*/}
                    {/*    </p>*/}
                    {/*</Support>*/}
                </Footer>
            </Hidden>
        </Root>
    );
};

export { Sidebar };
